/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import h from "util/dom-builder.js";
import util from "./util/util.js";
import Cookies from 'js-cookie';
import stream from "./util/stream.js";

var devMode = (location.hostname == "localhost");
var ROOT_URL = (location.hostname == "localhost") ? "http://localhost:8081/birdata/api" : "/api";
var AUTH_IFRAME_URL = (location.hostname == "localhost") ? "/empty.html" : "/app/empty.html";
var APP_ROOT_URL = (location.hostname == "localhost") ? "/" : "/app/";

// Single logout logic
if(Cookies.get('oidc_logout') == 'true') {
	Cookies.remove('oidc_logout');
}

var data = {};

function disableCacheForAdmin() {
	me(true).noLoginRedirect()
		.catch(function() {})
		.then(function(user) {
			if(user) {
				var isAdmin = user.is_administrator || user.permissions.some(p => p.role == "admin" );
				if(isAdmin) {
					// Adds a _u parameter to each request which prevents cached non-secure requests from overriding what admin should see
					settings.extraQueryParams['_u'] = user.id;
				}
			}
		})
}

function speciesList(params) {
	if(params) {
		return get("/species", params);
	}

	if(!data.speciesPromise) {
		data.speciesPromise = get("/species");
	}
	return data.speciesPromise;
}

function mySurveys(params) {
	return get("/surveys", params);
}

function userRecentSurveys(userId) {
	return get("/surveys/users/" + userId + "/recent");
}

function surveys(criteria) {
	return get("/surveys/all", criteria);
}

function surveyShareURL(id) {
	return get("/surveys/" + id + "/share_url");
}

function commonSpecies(lat, lon) {
	return get("/species/common", {
		lat: lat,
		lon: lon
	});
}

function commonSpeciesPrintURL(lat, lon) {
	return ROOT_URL + "/species/common?" + util.encodeParams({ lat: lat, lon: lon, format: "html" });
}

function surveysAtSharedSite(sharedSiteId) {
	return get("/surveys/all", { shared_site_id: sharedSiteId || -1 });
}

function surveyStats(criteria, statsType) {
	return get("/surveys/all", {
		summarize: statsType || "stats",
		...criteria
	});
}

function surveyTile(criteria, x, y, z) {
	criteria = {
		tile: [x,y,z].join(","),
		...criteria
	};
	delete criteria.xmin;
	delete criteria.xmax;
	delete criteria.ymin;
	delete criteria.ymax;
	return get("/surveys/all", criteria);
}

function surveySharedSiteSummaries(criteria) {
	return get("/surveys/all", {
		summarize: "sites",
		...criteria
	});
}

function getSecureURL(url, params) {
	return Promise.resolve(ROOT_URL + url + "?" + util.encodeParams(params));
}

function mySurveysExportURL(params, format) {
	return getSecureURL("/surveys", { format, ...params});
}

function surveysExportURL(params, format) {
	return getSecureURL("/surveys/all", { format, ...params});
}


function report(reportName, params) {
	if(reportName == 'tabular' || reportName == 'duckdb') {
		return get("/reports/" + reportName, params);
	}
	// Creates a report job, then returns a stream of polling the report status once per second
	return post("/reports/" + reportName + '?' + util.encodeParams(params)).then(function(result) {
		function next() {
			return get(result.url).then(function(status) {
				// Hack because it's currently hard to send an absolute URL from the server
				if(status.result_url) {
					status.result_url = ROOT_URL + status.result_url;
				}

				return {
					value: status,
					next: (status.result_url || status.failed) ? undefined : util.delay(2000).then(next)
				}
			})
		}

		return next();
	});
}

function reportURL(reportName, params) {
	return Promise.resolve(ROOT_URL + "/reports/" + reportName + "?" + util.encodeParams(params));
}

function survey(id, options) {
	return get("/surveys/" + id, options);
}

function deleteSurvey(id) {
	return del("/surveys/" + id);
}

function surveyPoints(params) {
	return get("/survey_points", params);
}

function surveyPoint(id) {
	return get("/survey_points/" + id);
}

function updateSurveyPoint(id, params) {
	return put("/survey_points/" + id, params);
}

function surveyPointRelocationCheck(id, params) {
	return get("/survey_points/" + id + "/relocation_check", params);
}

function mergeSurveyPoint(id, dest_id) {
	return post("/survey_points/" + id + "/merge", { dest_id: dest_id });
}

function sharedSites(params) {
	return getCached("/shared_sites", params);
}

function sharedSite(id, params) {
	return get("/shared_sites/" + id, params);
}

function sharedSitePrivateUsers(layerCode) {
	return get("/shared_sites/" + layerCode + "/private_users");
}

function sharedSitesAtPoint(lat, lon, params) {
	return get("/shared_sites", {
		lat: lat,
		lon: lon,
		...params
	});
}

function sharedSitesInRegion(regionId) {
	return get("/shared_sites", {
		region_id: regionId
	});
}

function sharedSiteTiles(x, y, z) {
	return get("/shared_sites/tile/" + z + "/" + y + "/" + x);
}

function sharedSiteLayers(params) {
	return get("/shared_sites/layers", params);
}

function sharedSiteSpecies(sharedSiteId) {
	return get("/shared_sites/" + sharedSiteId + "/species");
}

function updateSharedSite(sharedSite) {
	return put('/shared_sites/' + sharedSite.id, sharedSite);
}

function createSharedSite(layerCode, sharedSite) {
	return post('/shared_sites/' + layerCode, sharedSite);
}

function deleteSharedSite(sharedSiteId) {
	return del("/shared_sites/" + sharedSiteId);
}

function area(id) {
	return get("/areas/" + id);
}

function areas(params) {
	return get("/areas", params);
}

function areasAtPoint(lat, lon, params) {
	return get("/areas", {
		lat: lat,
		lon: lon,
		...params
	});
}

function significantAreas() {
	return get("/areas/s2020_significant");
}

function areaTiles(x, y, z) {
	return get("/areas/tile/" + z + "/" + y + "/" + x);
}

function areaLayers(params) {
	return get("/areas/layers", params);
}

function regions() {
	return get("/regions");
}

function regionTiles(x, y, z) {
	return get("/regions/tile/" + z + "/" + y + "/" + x);
}

function speciesRangeInfo(lat, lon) {
	return get("/species/range", {
		lat: lat,
		lon: lon
	});
}

// Pass cached=true if you don't mind getting a stale result
function me(cached) {
	if(cached && me.cached) {
		return me.cached;
	}
	me.cached = get("/users/me");
	return me.cached;
}

function myStats() {
	return get("/stats/me");
}

function stats(stat, params) {
	return get("/stats/" + stat, params);
}

function userStat(userId, stat) {
	return get("/stats/users/" + userId + "/" + stat).then(function(result) {
		return result[stat];
	});
}

function user(id, params) {
	return get("/users/" + id, params);
}

function users(params) {
	return get("/users", params);
}

function mergeUser(sourceId, targetId) {
	return post("/users/merge", {
		source_id: sourceId,
		target_id: targetId
	});
}

function deleteUser(userId) {
	return del("/users/" + userId)
}

function updatePermission(userId, permission) {
	return post("/users/" + userId + "/permissions", permission);
}

function mySpecies(params) {
	return get("/species/me", params);
}

function userSpecies(userId) {
	return get("/species/users/" + userId);
}

function speciesImageURL(id, imageType) {
	return [ROOT_URL, "species_image", id, imageType].join("/");
}

function loadSpeciesSprite() {
	if(!loadSpeciesSprite._called) {
		loadSpeciesSprite._called = true;

		var link = document.createElement( "link" );
		link.href = ROOT_URL + "/species_image/sprite.css"
		link.type = "text/css";
		link.rel = "stylesheet";
		document.getElementsByTagName( "head" )[0].appendChild( link );
	}
}

function vettableSightingsLegacy(params) {
	return get("/sightings/vettable_legacy", params);
}

function vettableSightings(params) {
	return get("/sightings/vettable", params);
}

function sightingsWithLocations(params) {
	return get("/sightings/with_locations", params);
}

function vettableSightingsSortOptions() {
	return get("/sightings/vettable/sort_options");
}

function vettableDensityTile(criteria, x, y, z) {
	criteria = {
		tile: [x,y,z].join(","),
		...criteria
	};
	delete criteria.xmin;
	delete criteria.xmax;
	delete criteria.ymin;
	delete criteria.ymax;
	return get("/sightings/vettable_density_tile", criteria);
}

function vettingStats(params) {
	return get("/sightings/vetting_stats", params);
}

function sighting(id) {
	return get("/sightings/" + id);
}

function powlTerritories(lat, lon) {
	return get("/powl/territories", { lat, lon });
}

var lookupPromise;

function lookup(type) {
	if(!lookupPromise) {
		lookupPromise = get("/lookups").then(addLookupFind);
	}

	if(type) {
		return lookupPromise.then(function(lookups) {
			return lookups[type] || [];
		});
	} else {
		return lookupPromise;
	}
}

function refreshLookups() {
	lookupPromise = get("/lookups", { _options: { headers: { 'Cache-Control': 'max-age=0', 'X-Invalidate-Cache': 'true' }}}).then(addLookupFind);
	// Return promise so we can wait for refresh to happen
	return lookupPromise;
}

function addLookupFind(lookups) {
	lookups.find = function(type, code) {
		return (lookups[type] || []).find(x => x.code === code);
	};

	return lookups;
}

function locationSearch(search, xmin, ymin, xmax, ymax) {
	var params = {};
	if(xmin && ymin && xmax && ymax) {
		params = {
			xmin: xmin,
			ymin: ymin,
			xmax: xmax,
			ymax: ymax
		};
	}
	params.q = search;

	return get("/search/location", params);
}

function submitSurvey(survey, updateExisting) {
	if(updateExisting) {
		return put('/surveys/' + survey.id, survey);
	} else {
		return post('/surveys', survey);
	}
}

function vettingReviews(sightingId) {
	return get("/sightings/" + sightingId + "/vetting_reviews");
}

function submitVettingReview(sightingId, vettingReview) {
	return post('/sightings/' + sightingId + '/vetting_reviews', vettingReview);
}

function submitBulkVettingReview(vettingReview) {
	return post('/sightings/vetting_reviews', vettingReview);
}

function submitFeedback(feedback) {
	return post('/feedback', feedback);
}

function reallocateSurveys(surveyIds, userId) {
	return post('/surveys/reallocate', {
		survey_ids: surveyIds,
		user_id: userId
	});
}

function bulkSurveyUpdate(surveyIds, params) {
	return post('/surveys/bulk_update', {
		survey_ids: surveyIds,
		...params
	});
}

function dataImport(id) {
	return get('/imports/' + id);
}

function imports() {
	return get('/imports');
}

function createImport(importData) {
	return post('/imports', importData)
}

function updateImport(id, importData) {
	return put('/imports/' + id, importData);
}

function importProgress(id) {
	return get('/imports/' + id + '/progress', { _options: { stream: true }});
}

function importIssues(id) {
	return get('/imports/' + id + '/issues');
}

// On success, returns updated lookups from server
function addLookupItem(lookup, data) {
	return post('/lookups/' + lookup, data).then(function(result) {
		return refreshLookups();
	});
}

function submitQuizResult(data) {
	return post('/users/me/quiz_results', data);
}

function passedQuiz(quiz) {
	return get('/users/me/quiz_results/' + quiz + '/passed');
}

function events(params) {
	return get('/events', params);
}

function event(id) {
	return get('/events/' + id);
}

function eventSiteAvailability(id) {
	return get('/events/' + id + '/site_availability');
}

function createEvent(event) {
	return post('/events', event);
}

function deleteEvent(eventId) {
	return del('/events/' + eventId);
}

function updateEvent(event) {
	return put('/events/' + event.id, event);
}

function registerForEvent(event) {
	return post('/events/' + event.id + '/registrations', {})
}

function unregisterForEvent(event) {
	return me(true).then(me => {
		return del('/events/' + event.id + '/registrations/' + me.id);
	});
}

function eventBookings(id) {
	return get('/events/' + id + "/bookings");
}

function eventBookingsExportURL(eventId) {
	return getSecureURL("/events/" + eventId + "/bookings", { format: 'csv' });
}

function eventBooking(code) {
	return get('/events/bookings/' + code);
}

function bookingCalendarEventURL(code) {
	return ROOT_URL + "/events/bookings/" + code + ".ics";
}

function joinBooking(code) {
	return post('/events/bookings/' +  code );
}

function eventRegistrations(id) {
	return get('/events/' + id + "/registrations");
}

function eventRegistrationsExportURL(eventId) {
	return getSecureURL("/events/" + eventId + "/registrations", { format: 'csv' });
}

function createBooking(eventId, booking) {
	return post('/events/' + eventId + '/bookings', booking);
}

function cancelBooking(eventId, bookingId) {
	return del('/events/' + eventId + '/bookings/' + bookingId);
}

function runQuery(query) {
	return get('/query/sql', { q: query });
}

function upload(file) {
	return post("/uploads", file, file.type).then(function(result) {
		return ROOT_URL + "/uploads/" + result.uuid;
	});
}

function remoteUpload(file, statusCallback, progressCallback, options) {
	if(!statusCallback) statusCallback = function(){};
	if(!progressCallback) progressCallback = function(){};

	statusCallback('init');
	return post("/uploads/remote", { content_type: file.type }).then(function(result) {
		statusCallback('uploading');
		var xhr = new XMLHttpRequest();

		xhr.open(result.upload_method, result.upload_url);
		xhr.setRequestHeader('Content-Type', file.type);
		xhr.setRequestHeader('Content-Disposition', 'attachment; filename=' + JSON.stringify(file.name));

		if(options && options.compress && window.CompressionStream) {
			try {
				var compressedReadableStream = file.stream().pipeThrough(new CompressionStream('gzip'));
				var blob = new Response(compressedReadableStream).blob();
				file = blob;
				xhr.setRequestHeader('Content-Encoding', 'gzip');
			} catch(error) {
				console.warn("Failed to compress file for upload; continuing without compression");
			}
		}

		xhr.upload.addEventListener("progress", function(evt) {
			if(evt.lengthComputable) {
				progressCallback(evt.loaded / evt.total);
			}
		});


		return Promise.resolve(file)
		.then((f) => xhrPromise(xhr, f))
		.then(function() {
			statusCallback('finishing');
			// Intentional nesting
			return put("/uploads/remote/" + result.uuid).then(function() {
				statusCallback('complete');
				return ROOT_URL + "/uploads/" + result.uuid;
			});
		});
	});
}

function uploadURL(uuid) {
	return ROOT_URL + "/uploads/" + uuid;
}

function xhrPromise(xhr, dataToSend) {
	return new Promise(function(resolve, reject) {
		xhr.addEventListener("error", function() {
			reject(XHRError(xhr));
		});
		xhr.addEventListener("load", function(evt) {
			// Check for error
			if(xhr.status >= 200 && xhr.status < 300) {
				resolve(xhr);
			} else {
				reject(XHRError(xhr));
			}
		});
		xhr.send(dataToSend);
	});
}

function isXHRError(e) {
	return 'xhr' in e;
}

function XHRError(xhr) {
	var url = xhr._meta ? xhr._meta.url : '?';
	var error = new Error("XHR Error " + xhr.status + ": " + url);
	error.xhr = xhr;
	return error;
}

function attemptGet(url, params, skipAuth) {
	params = params || {};
	var options = params._options || {};

	params = {
		...settings.extraQueryParams,
		...params
	};
	delete params._options;

	if(options.public) {
		delete params._u;
		params._public = true;
	}

	if(Object.keys(params).length > 0) {
		url += "?" + util.encodeParams(params);
	}

	if(!url.match(/^([a-z]+:)?\/\//)) {
		url = ROOT_URL + url;
	}

	var xhr = new XMLHttpRequest();
	xhr.open('GET', url);

	// Used for debugging (e.g. in exceptions)
	xhr._meta = {
		url: url,
		method: 'GET',
		params: params,
		options: options
	};

	xhr.responseType = '';
	xhr.withCredentials = true;

	for(const [k, v] of Object.entries(options.headers || {})) {
		xhr.setRequestHeader(k, v);
	};

	if(options.stream) {
		return xhrStream(xhr);
	} else {
		return xhrPromise(xhr).then(function(xhr) {
			var response = xhr.responseText;
			// Attempt to parse as JSON
			try { response = JSON.parse(response); } catch(e) {}
			return response;
		});
	}
}


// A stream is a promise which resolves to [item, promise(rest of stream)]
function xhrStream(xhr) {
	var prevText = '';

	return stream.fromEventEmitter(function(next, finished, error) {
		xhr.addEventListener("progress", function(evt) {
			var chunk = xhr.responseText.substr(prevText.length);
			prevText = xhr.responseText;
			next(chunk);
		});
		xhr.addEventListener("error", function() { error(xhr); });
		xhr.addEventListener("load", function() { finished(prevText); });

		xhr.send();
	});
}

function getCached(url, params) {
	var cache = getCached._cache || {};
	getCached._cache = cache;

	var key = url;
	if(params) {
		key += '&' + util.encodeParams(params);
	}

	var result = cache[key] || get(url, params);
	cache[key] = result;

	return result;
}

function get(url, params, skipAuth) {
	console.log("GET: " + url + " " + JSON.stringify(params || {}));

	var noLoginRedirect = false;

	// Attempt request
	var promise = attemptGet(url, params, skipAuth).catch(function(e) {
		if(isXHRError(e)) {
			// If it fails (e.g. due to expired token), get a new token and try again.
			return handleAjaxError(e.xhr, skipAuth, noLoginRedirect).then(function() {
				// If it still fails, give up
				return attemptGet(url, params, skipAuth);
			});
		} else {
			throw e;
		}
	});

	// Add chainable method to returned promise that allows us to prevent a login redirect
	// from happening when user is not logged in
	promise.noLoginRedirect = function() {
		noLoginRedirect = true;
		return promise;
	};

	return promise;
}

function putOrPost(url, data, contentType, method, noRetry) {
	if(!contentType) {
		contentType = 'application/json';
	}

	if(contentType == 'application/json' && typeof data !== 'string') {
		data = JSON.stringify(data);
	}

	var xhr = new XMLHttpRequest();
	xhr.open(method, ROOT_URL + url);
	xhr.withCredentials = true;
	xhr.setRequestHeader("Content-type", contentType);

	return xhrPromise(xhr, data).then(function(xhr) {
		var response = xhr.responseText;
		// Attempt to parse as JSON
		try { response = JSON.parse(response); } catch(e) {}
		return response;
	}).catch(e => {
		if(isXHRError(e)) {
			if(noRetry) {
				throw XHRError(e.xhr);
			} else {
				return handleAjaxError(e.xhr, false).then(function() {
					return putOrPost(url, data, contentType, method, true);
				});
			}
		} else {
			throw e;
		}
	});
}

function post(url, data, contentType) {
	return putOrPost(url, data, contentType, 'POST');
}

function put(url, data, contentType) {
	return putOrPost(url, data, contentType, 'PUT');
}

function del(url, retry = true) {
	var xhr = new XMLHttpRequest();
	xhr.open('DELETE', ROOT_URL + url);

	xhr.withCredentials = true;

	return xhrPromise(xhr).catch(e => {
		if(isXHRError(e)) {
			return handleAjaxError(e.xhr, false, false).then(() => {
				return del(url, false);
			});
		} else {
			throw e;
		}
	});
}

function loginUrlFromResponse(xhr) {
	var response = JSON.parse(xhr.responseText);

	var loginUrl;
	response.errors.forEach(function(error) {
		if(error.loginUrl) {
			loginUrl = error.loginUrl;
		}
	});
	return loginUrl;
}

function handleAjaxError(xhr, skipAuth, noLoginRedirect) {
	if(devMode && xhr.status == 401 && !skipAuth && !noLoginRedirect && !window.location.search.match(/[?&]error=invalid_request/)) {
		window.location.href = ROOT_URL + "/auth/login?redirect_uri=" + encodeURIComponent(window.location.href);
	}

	var e = XHRError(xhr);
	console.log(xhr);
	return Promise.reject(e);
}

function extendParams(url, params) {
	var existingParams = util.parseParams(url.split(/[?#]/)[1] || "");
	var newParams = { ...params, ...existingParams };
	return url.replace(/([^?#]*)([^#]*)(.*)/, function(all, prefix, query, suffix) { return prefix + "?" + util.encodeParams(newParams) + suffix; });
}

var settings = {
	extraQueryParams: {}
};

disableCacheForAdmin()

export default {
	survey,
	surveysExportURL,
	surveyShareURL,
	mySurveys,
	mySurveysExportURL,
	userRecentSurveys,
	deleteSurvey,
	surveys,
	surveysAtSharedSite,
	speciesList,
	surveyPoint,
	surveyPoints,
	updateSurveyPoint,
	mergeSurveyPoint,
	surveyPointRelocationCheck,
	surveyStats,
	surveyTile,
	surveySharedSiteSummaries,
	settings,
	sharedSites,
	sharedSite,
	sharedSitePrivateUsers,
	sharedSitesAtPoint,
	sharedSitesInRegion,
	sharedSiteTiles,
	sharedSiteLayers,
	sharedSiteSpecies,
	updateSharedSite,
	createSharedSite,
	deleteSharedSite,
	area,
	areas,
	areaTiles,
	areaLayers,
	areasAtPoint,
	significantAreas,
	regions,
	regionTiles,
	lookup,
	addLookupItem,
	refreshLookups,
	speciesRangeInfo,
	locationSearch,
	myStats,
	stats,
	userStat,
	me,
	user,
	users,
	mergeUser,
	deleteUser,
	updatePermission,
	mySpecies,
	userSpecies,
	speciesImageURL, // TODO: Deprecate in favour of sprite
	loadSpeciesSprite,
	submitSurvey,
	upload,
	remoteUpload,
	uploadURL,
	vettableSightingsLegacy,
	vettableSightings,
	vettableSightingsSortOptions,
	vettableDensityTile,
	sightingsWithLocations,
	vettingStats,
	vettingReviews,
	submitVettingReview,
	submitBulkVettingReview,
	sighting,
	submitFeedback,
	reallocateSurveys,
	bulkSurveyUpdate,
	commonSpecies,
	commonSpeciesPrintURL,
	dataImport,
	imports,
	createImport,
	updateImport,
	importProgress,
	importIssues,
	report,
	reportURL,
	submitQuizResult,
	passedQuiz,
	powlTerritories,
	events,
	createEvent,
	deleteEvent,
	updateEvent,
	registerForEvent,
	unregisterForEvent,
	event,
	eventSiteAvailability,
	eventBookings,
	eventBookingsExportURL,
	eventBooking,
	bookingCalendarEventURL,
	joinBooking,
	eventRegistrations,
	eventRegistrationsExportURL,
	createBooking,
	cancelBooking,
	runQuery,
	APP_ROOT_URL
};
